import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import AuthCallback from './pages/AuthCallback';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import OnboardPage from './pages/OnboardPage';
import InviteRedemptionPage from './pages/InviteRedemptionPage';
import CompleteProfilePage from './pages/CompleteProfilePage';
import DirectoryPage from './pages/DirectoryPage';
import RulesPage from './pages/RulesPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import RegisterPage, { ForgotPasswordPageC1 } from './pages/RegisterPage';
import WelcomePage from './pages/WelcomePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ClaimAccountPage from './pages/ClaimAccountPage';
import LadderPage from './pages/LadderPage';
import PlayerPage from './pages/PlayerPage';
import ChallengesPage from './pages/ChallengesPage';
import PaySuccessPage from './pages/PaySuccessPage';
import PayCancelPage from './pages/PayCancelPage';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import PlayerSessionDetailPage from './pages/PlayerSessionDetailPage';
import AdminEventEditorPage from './pages/AdminEventEditorPage';
import AdminEventsHubPage from './pages/AdminEventsHubPage';
import AdminEventCreatePage from './pages/AdminEventCreatePage';
import AdminLadderManagePage from './pages/AdminLadderManagePage';
import MembersPage from './pages/MembersPage';
import AdminCommunicationsPage from './pages/AdminCommunicationsPage';
// Phase C1.5b — MyProfilePage removed as an active route; /profile/:id
// now redirects to the canonical /player/:id (PlayerPage). Kept off the
// bundle to keep two edit-profile experiences from co-existing.
import TournamentsListPage from './pages/TournamentsListPage';
import TournamentDetailPage from './pages/TournamentDetailPage';
import TournamentManagePage from './pages/TournamentManagePage';
import TournamentViewModePage from './pages/TournamentViewModePage';
import RoundRobinDetailPage from './pages/RoundRobinDetailPage';
import RoundRobinManagePage from './pages/RoundRobinManagePage';
import RoundRobinViewModePage from './pages/RoundRobinViewModePage';
import ClubEventDetailPage from './pages/ClubEventDetailPage';
import AdminClubEventEditorPage from './pages/AdminClubEventEditorPage';
import HomePage from './pages/HomePage';
import { PrivacyPolicyPage, TermsOfServicePage, SmsTermsPage } from './pages/LegalPages';
import JoinPage from './pages/JoinPage';
import RegistrationPendingPage from './pages/RegistrationPendingPage';
import BottomNav from './components/BottomNav';
import SiteFooter from './components/SiteFooter';
import ImpersonationBanner from './components/ImpersonationBanner';
import AdminNav from './components/admin/AdminNav';
import Avatar from './components/Avatar';
import { BookOpen } from '@phosphor-icons/react';
import rcMark from './assets/rc-mark.png';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, profileCompleted, accountStatus, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fbfaf8]">
        <div className="w-8 h-8 border-2 border-[#015e3f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  // Public-registration users live on the pending screen until an admin
  // approves them.
  if (accountStatus === 'pending_approval' || accountStatus === 'denied') {
    return <Navigate to="/registration-pending" replace />;
  }
  // Approved RTC members can access the app even without Singles Ladder
  // membership. Ladder access is a separate admin-controlled property and
  // is gated INSIDE the Ladder / Challenges pages, not at the route level.
  if (!profileCompleted) return <Navigate to="/complete-profile" replace />;

  return children;
}

// Admin-only wrapper (Phase PR1). Non-admins are redirected to home.
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

// Phase C1.5b — /profile/:id → /player/:id (canonical). The player route
// accepts EITHER a players.id OR a profiles.id, so this preserves any
// bookmarked directory link.
//
// Phase C1.6 — Wait for the auth context to finish loading before deciding
// where to send the user. Without this, a hard refresh on /profile/me
// briefly saw `user === null`, redirected to /login, which then bounced the
// already-authenticated user back to /. The loading gate keeps the redirect
// deterministic on cold loads.
function ProfileRedirect() {
  const { profileId } = useParams();
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fbfaf8]">
        <div className="w-8 h-8 border-2 border-[#015e3f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  const target = profileId && profileId !== 'me' ? profileId : user?.id;
  if (!target) return <Navigate to="/login" replace />;
  return <Navigate to={`/player/${target}`} replace />;
}

function AppLayout({ children }) {
  const location = useLocation();
  const { user } = useAuth();
  // Persistent Admin nav: show on every admin-prefixed route (and the
  // tournament/round-robin MANAGE surfaces), but only for admins — so it stays
  // hidden from members and during impersonation (impersonated role !== admin).
  const path = location.pathname;
  const isAdminRoute = path.startsWith('/admin')
    || /^\/tournaments\/[^/]+\/manage$/.test(path)
    || /^\/round-robins\/[^/]+\/manage$/.test(path);
  const showAdminNav = isAdminRoute && user?.role === 'admin';
  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#fbfaf8] pb-24 relative sm:max-w-xl md:max-w-2xl lg:max-w-4xl">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#fbfaf8]/80 border-b border-[#e3dcd1] px-6 py-3">
        <AppHeader />
      </header>
      {showAdminNav && (
        <div className="px-6 pt-4" data-testid="admin-nav-wrap">
          <AdminNav />
        </div>
      )}
      <main className="p-6">
        {children}
      </main>
      <SiteFooter />
      <BottomNav />
    </div>
  );
}

function AppHeader() {
  const { user, player } = useAuth();
  const navigate = useNavigate();

  const goToMyProfile = () => {
    // Canonical rich profile lives at /player/:id. The backend accepts
    // either a players.id OR a profiles.id, so this works for admins and
    // non-ladder members too.
    navigate(`/player/${user?.id}`);
  };

  return (
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')} data-testid="app-header-brand">
        <img
          src={rcMark}
          alt="The Rippers Club"
          className="w-8 h-8 object-contain"
        />
        <span className="font-serif text-lg text-[#013523] tracking-tight">The Rippers Club</span>
      </div>
      {user && (
        <div className="flex items-center gap-3">
          <button
            data-testid="nav-rules-header"
            onClick={() => navigate('/rules')}
            className="text-[#6a766e] hover:text-[#015e3f] transition-colors"
            title="Rules"
          >
            <BookOpen size={20} weight="regular" />
          </button>
          <button
            type="button"
            data-testid="nav-profile-header"
            onClick={goToMyProfile}
            aria-label="My profile"
            className="rounded-full focus:outline-none focus:ring-2 focus:ring-[#015e3f]/30 transition-shadow"
          >
            <Avatar
              src={user.avatar_url}
              name={user.first_name || user.name}
              size="sm"
              testId="nav-profile-avatar"
            />
          </button>
        </div>
      )}
    </div>
  );
}

function SignupRoute() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const code = params.get('code');
  if (code) {
    // Single-screen onboarding when invited via link
    return <Navigate to={`/onboard?code=${encodeURIComponent(code)}`} replace />;
  }
  return <SignupPage />;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Supabase password-recovery emails return a hash like
  //   #access_token=...&refresh_token=...&type=recovery
  // or an error hash like
  //   #error=access_denied&error_code=otp_expired&error_description=...
  // The hash may land on ANY route depending on the Supabase project's
  // Site URL config. Funnel both happy and error cases into /reset-password
  // (preserving the hash) so the user never gets bounced to /login mid-flow.
  const hash = location.hash || '';
  const isRecoveryHash =
    hash.includes('type=recovery') ||
    /[#&?]error(_code|_description)?=/.test(hash);
  if (isRecoveryHash && location.pathname !== '/reset-password') {
    return <Navigate to={{ pathname: '/reset-password', hash }} replace />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/sms-terms" element={<SmsTermsPage />} />
      <Route path="/join" element={<JoinPage />} />
      <Route path="/register-legacy" element={<JoinPage />} />
      <Route path="/registration-pending" element={<RegistrationPendingPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPageC1 />} />
      <Route path="/forgot-password-legacy" element={<ForgotPasswordPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/welcome" element={<ProtectedRoute><AppLayout><WelcomePage /></AppLayout></ProtectedRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/claim-account" element={<ClaimAccountPage />} />
      <Route path="/signup" element={<SignupRoute />} />
      <Route path="/onboard" element={<OnboardPage />} />
      <Route path="/redeem-invite" element={<InviteRedemptionPage />} />
      <Route path="/complete-profile" element={<CompleteProfilePage />} />
      {/* Stripe Checkout return routes — auth-gated because the poll
          endpoint requires an authenticated caller anyway. */}
      <Route path="/pay/success" element={<ProtectedRoute><AppLayout><PaySuccessPage /></AppLayout></ProtectedRoute>} />
      <Route path="/pay/cancel"  element={<ProtectedRoute><AppLayout><PayCancelPage /></AppLayout></ProtectedRoute>} />
      <Route
        path="/directory"
        element={
          <ProtectedRoute>
            <AppLayout><DirectoryPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rules"
        element={
          <ProtectedRoute>
            <AppLayout><RulesPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout><HomePage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ladder"
        element={
          <ProtectedRoute>
            <AppLayout><LadderPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ladder/:slug"
        element={
          <ProtectedRoute>
            <AppLayout><LadderPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/player/:playerId"
        element={
          <ProtectedRoute>
            <AppLayout><PlayerPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/challenges"
        element={
          <ProtectedRoute>
            <AppLayout><ChallengesPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events"
        element={
          <ProtectedRoute>
            <AppLayout><EventsPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/sessions/:sessionId"
        element={
          <ProtectedRoute>
            <AppLayout><PlayerSessionDetailPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:slug"
        element={
          <ProtectedRoute>
            <AppLayout><EventDetailPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AppLayout><SettingsPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AppLayout><AdminPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/events"
        element={
          <ProtectedRoute>
            <AppLayout><AdminEventsHubPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/events/new"
        element={
          <ProtectedRoute>
            <AppLayout><AdminEventCreatePage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/events/:sessionId"
        element={
          <ProtectedRoute>
            <AppLayout><AdminEventEditorPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/members"
        element={
          <ProtectedRoute>
            <AppLayout><MembersPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/communications"
        element={<AdminRoute><AppLayout><AdminCommunicationsPage /></AppLayout></AdminRoute>}
      />
      <Route
        path="/admin/ladders/:slug/manage"
        element={<AdminRoute><AppLayout><AdminLadderManagePage /></AppLayout></AdminRoute>}
      />
      <Route
        path="/profile/me"
        element={<ProfileRedirect />}
      />
      <Route
        path="/profile/:profileId"
        element={<ProfileRedirect />}
      />
      <Route
        path="/admin/presentation-center/*"
        element={<Navigate to="/admin" replace />}
      />
      <Route
        path="/tournaments"
        element={
          <ProtectedRoute>
            <AppLayout><TournamentsListPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments/:id"
        element={
          <ProtectedRoute>
            <AppLayout><TournamentDetailPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments/:id/manage"
        element={
          <ProtectedRoute>
            <AppLayout><TournamentManagePage /></AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Admin-only TV/presentation surface — no AppLayout so there is no
          BottomNav, header, or side chrome. Non-admins are redirected
          to `/` by <AdminRoute>. */}
      <Route
        path="/tournaments/:id/view"
        element={<AdminRoute><TournamentViewModePage /></AdminRoute>}
      />
      <Route
        path="/round-robins/:id"
        element={
          <ProtectedRoute>
            <AppLayout><RoundRobinDetailPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/round-robins/:id/manage"
        element={
          <ProtectedRoute>
            <AppLayout><RoundRobinManagePage /></AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Admin-only TV/presentation surface for round robins — same
          rationale as the tournament view route above. */}
      <Route
        path="/round-robins/:id/view"
        element={<AdminRoute><RoundRobinViewModePage /></AdminRoute>}
      />
      <Route
        path="/club-events/:id"
        element={
          <AppLayout><ClubEventDetailPage /></AppLayout>
        }
      />
      <Route
        path="/admin/club-events/new"
        element={
          <ProtectedRoute>
            <AppLayout><AdminClubEventEditorPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/club-events/:id"
        element={
          <ProtectedRoute>
            <AppLayout><AdminClubEventEditorPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ImpersonationBanner />
        <AppRouter />
        <Toaster
          position="top-center"
          toastOptions={{
            style: {
              background: '#fbfaf8',
              border: '1px solid #e3dcd1',
              color: '#013523',
              fontFamily: 'Outfit, sans-serif',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
